
import { createContext, useState, useEffect, ReactNode } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { checkAndRefreshPremiumStatus } from '@/utils/diary/storage';
import { toast } from '@/hooks/use-toast';

interface AuthContextProps {
  user: Session['user'] | null | undefined;
  session: Session | null | undefined;
  premium: boolean;
  isPremium: boolean; // Added for compatibility
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName?: string) => Promise<void>; // Added signUp method
  signInWithProvider: (provider: "google" | "github") => Promise<void>; // Renamed for clarity
  signOut: () => Promise<void>;
  refreshPremiumStatus: () => Promise<void>;
  isLoading: boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

// Export the AuthContext so it can be imported in hooks.ts
export const AuthContext = createContext<AuthContextProps>({
  user: null,
  session: null,
  premium: false,
  isPremium: false, // Added for compatibility
  signIn: async () => {},
  signUp: async () => {}, // Added default implementation
  signInWithProvider: async () => {}, // Renamed for clarity
  signOut: async () => {},
  refreshPremiumStatus: async () => {},
  isLoading: true,
});

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<Session['user'] | null | undefined>(undefined);
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [premium, setPremium] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  // Track authentication activity
  const [lastActivity, setLastActivity] = useState<number>(Date.now());
  const SESSION_TIMEOUT = 60 * 60 * 1000; // 1 hour in milliseconds
  
  // Update activity timestamp on user interaction
  const updateActivity = () => {
    setLastActivity(Date.now());
  };
  
  // Set up activity listeners
  useEffect(() => {
    // Add event listeners to track user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, updateActivity);
    });
    
    // Auto-logout check timer
    const intervalId = setInterval(() => {
      const now = Date.now();
      if (user && now - lastActivity > SESSION_TIMEOUT) {
        // Auto logout after inactivity
        console.log('Session timeout due to inactivity');
        signOut();
        toast({
          title: 'Session expired',
          description: 'You have been logged out due to inactivity',
          variant: 'default',
        });
      }
    }, 60000); // Check every minute
    
    return () => {
      // Cleanup event listeners
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
      clearInterval(intervalId);
    };
  }, [user, lastActivity]);

  useEffect(() => {
    // First set up auth state listener, then check for existing session
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setSession(session ?? null);
      
      // Automatically refresh activity on auth state change
      updateActivity();
    });

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setSession(session ?? null);
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const getPremiumStatus = async () => {
      if (user?.id) {
        const isPremium = await checkAndRefreshPremiumStatus(user.id);
        setPremium(isPremium);
      }
    };

    getPremiumStatus();
  }, [user]);

  // Add email/password sign in method
  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (error) {
      throw error;
    }
    
    // Reset activity timer on successful login
    updateActivity();
  };

  // Add sign up method
  const signUp = async (email: string, password: string, fullName?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName || ''
        }
      }
    });
    
    if (error) {
      throw error;
    }
  };

  // Rename to signInWithProvider for clarity
  const signInWithProvider = async (provider: "google" | "github") => {
    await supabase.auth.signInWithOAuth({
      provider: provider,
      options: {
        redirectTo: `${window.location.origin}/entries`
      }
    });
    
    // Reset activity timer on provider login redirect
    updateActivity();
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setPremium(false);
  };

  const refreshPremiumStatus = async (): Promise<void> => {
    if (!user?.id) return;
    
    try {
      console.log("Checking premium status for user:", user.id);
      const isPremium = await checkAndRefreshPremiumStatus(user.id);
      
      if (isPremium !== premium) {
        setPremium(isPremium);
      }
    } catch (error) {
      console.error("Error refreshing premium status:", error);
    }
  };

  const authValue: AuthContextProps = {
    user,
    session,
    premium,
    isPremium: premium, // Added alias for compatibility
    signIn,
    signUp, // Add the new signUp function
    signInWithProvider, // Renamed for clarity
    signOut,
    refreshPremiumStatus,
    isLoading,
  };

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
};
